/**
 * Parses a Cloudinary URL to extract base URL and asset path
 * Format: https://res.cloudinary.com/{cloud_name}/image/upload/{transformations}/{path}
 */
export interface CloudinaryUrlParts {
  baseUrl: string;
  assetPath: string;
  cloudName: string;
}

export function parseCloudinaryUrl(url: string): CloudinaryUrlParts | null {
  if (!url || !url.includes('cloudinary.com')) {
    return null;
  }

  try {
    const parts = url.split('/upload/');
    if (parts.length !== 2) {
      return null;
    }

    const [baseUrl, afterUpload] = parts;

    // Extract cloud name from base URL
    // Format: https://res.cloudinary.com/{cloud_name}/image
    const cloudNameMatch = baseUrl.match(/cloudinary\.com\/([^\/]+)/);
    const cloudName = cloudNameMatch ? cloudNameMatch[1] : '';

    // Split the part after 'upload/' to separate transformations from the asset path
    const segments = afterUpload.split('/');

    // Find where the actual file path starts (after transformations)
    // Transformations contain commas or underscore parameters (e.g., c_crop, w_100)
    let filePathStartIndex = 0;
    for (let i = 0; i < segments.length; i++) {
      // If segment contains transformation syntax, it's a transformation
      if (segments[i].includes(',') || segments[i].match(/^[a-z]_/)) {
        filePathStartIndex = i + 1;
      } else {
        // Once we hit a segment without transformation syntax, we've found the file path
        break;
      }
    }

    // Get the file path (version + path)
    const assetPath = segments.slice(filePathStartIndex).join('/');

    // The baseUrl already contains the resource type (e.g., /image)
    // So we just need to append /upload
    return {
      baseUrl: `${baseUrl}/upload`,
      assetPath,
      cloudName,
    };
  } catch (error) {
    console.error('Error parsing Cloudinary URL:', error);
    return null;
  }
}

/**
 * Extracts crop parameters from a Cloudinary URL
 * Returns the crop region if found
 */
export function extractCropFromUrl(url: string): { x: number; y: number; width: number; height: number } | null {
  if (!url || !url.includes('cloudinary.com')) {
    return null;
  }

  try {
    // Look for c_crop transformation with x_, y_, w_, h_ parameters
    const cropMatch = url.match(/c_crop,x_(\d+),y_(\d+),w_(\d+),h_(\d+)/);

    if (cropMatch) {
      return {
        x: parseInt(cropMatch[1]),
        y: parseInt(cropMatch[2]),
        width: parseInt(cropMatch[3]),
        height: parseInt(cropMatch[4]),
      };
    }

    return null;
  } catch (error) {
    console.error('Error extracting crop from URL:', error);
    return null;
  }
}

/**
 * Extracts image dimensions from a Cloudinary URL
 * Looks for w_ and h_ transformation parameters
 */
export function extractImageDimensions(url: string): { width: number; height: number } | null {
  if (!url || !url.includes('cloudinary.com')) {
    return null;
  }

  try {
    const parts = url.split('/upload/');
    if (parts.length !== 2) {
      return null;
    }

    const [, afterUpload] = parts;
    const segments = afterUpload.split('/');

    let width: number | null = null;
    let height: number | null = null;

    // Look through transformation segments for width and height
    for (const segment of segments) {
      const params = segment.split(',');
      for (const param of params) {
        if (param.startsWith('w_')) {
          const w = parseInt(param.substring(2));
          if (!isNaN(w)) width = w;
        }
        if (param.startsWith('h_')) {
          const h = parseInt(param.substring(2));
          if (!isNaN(h)) height = h;
        }
      }
    }

    if (width && height) {
      return { width, height };
    }

    return null;
  } catch (error) {
    console.error('Error extracting image dimensions:', error);
    return null;
  }
}

/**
 * Extracts annotation (layer overlay) from a Cloudinary URL
 * Returns annotation in ABSOLUTE coordinates (relative to full image)
 */
export function extractAnnotationFromUrl(url: string, currentCrop?: { x: number; y: number; width: number; height: number }): {
  x: number;
  y: number;
  width: number;
  height: number;
} | null {
  if (!url || !url.includes('cloudinary.com')) {
    return null;
  }

  try {
    console.log('🔍 [extractAnnotationFromUrl] Parsing URL for annotation:', url);

    // Look for layer overlay pattern: l_{layer},w_{width},h_{height},...,x_{x},y_{y}
    // The x,y in the URL are RELATIVE to the crop, so we need to convert to absolute

    // Find the layer section - it starts with l_ and ends with the asset path
    // Example: l_one_pixel_gidbyp,w_423,h_111,e_colorize,co_rgb:84cc16,o_15,bo_2px_solid_rgb:84cc16,fl_layer_apply,g_north_west,x_23,y_622
    const layerMatch = url.match(/l_[^\/]+/);
    if (!layerMatch) {
      console.log('❌ No layer found in URL');
      return null;
    }

    const layerString = layerMatch[0];
    console.log('📦 Found layer string:', layerString);

    // Extract width and height
    const widthMatch = layerString.match(/w_(\d+)/);
    const heightMatch = layerString.match(/h_(\d+)/);

    if (!widthMatch || !heightMatch) {
      console.log('❌ Width or height not found in layer');
      return null;
    }

    const width = parseInt(widthMatch[1]);
    const height = parseInt(heightMatch[1]);
    console.log('📏 Extracted dimensions:', { width, height });

    // Extract x and y position (after fl_layer_apply)
    // The pattern is: fl_layer_apply,g_north_west,x_{x},y_{y}
    const xMatch = layerString.match(/x_(\d+)/);
    const yMatch = layerString.match(/y_(\d+)/);

    if (!xMatch || !yMatch) {
      console.log('❌ Position (x,y) not found in layer');
      return null;
    }

    const relativeX = parseInt(xMatch[1]);
    const relativeY = parseInt(yMatch[1]);
    console.log('📍 Extracted position (relative to crop):', { relativeX, relativeY });

    // If we have the current crop, convert to absolute coordinates
    if (currentCrop) {
      const absoluteAnnotation = {
        x: currentCrop.x + relativeX,
        y: currentCrop.y + relativeY,
        width,
        height,
      };
      console.log('✅ Converted to absolute coordinates:', absoluteAnnotation);
      return absoluteAnnotation;
    }

    // If no crop provided, return as-is (assuming they're already absolute)
    const annotation = {
      x: relativeX,
      y: relativeY,
      width,
      height,
    };
    console.log('✅ Returning annotation (no crop conversion):', annotation);
    return annotation;
  } catch (error) {
    console.error('❌ Error extracting annotation from URL:', error);
    return null;
  }
}

/**
 * Extracts marker position from a Cloudinary URL
 * Looks for text layer overlay with fl_layer_apply,g_north_west,x_{x},y_{y}
 * Returns coordinates in ORIGINAL IMAGE SPACE (not scaled output)
 */
export function extractMarkerFromUrl(url: string): { x: number; y: number } | null {
  if (!url || !url.includes('cloudinary.com')) {
    return null;
  }

  try {
    // Look for text layer with marker pattern: l_text:...,fl_layer_apply,g_north_west,x_{x},y_{y}
    // Example: l_text:Roboto_24_bold:%E2%9E%9C,co_rgb:000000,b_rgb:d8e364/fl_layer_apply,g_north_west,x_402,y_11
    const markerMatch = url.match(/l_text:[^\/]+\/fl_layer_apply,g_north_west,x_(\d+),y_(\d+)/);

    if (!markerMatch) {
      return null;
    }

    // Marker position relative to scaled output
    const markerX = parseInt(markerMatch[1]);
    const markerY = parseInt(markerMatch[2]);

    // Extract crop and output dimensions to convert to original coordinates
    const crop = extractCropFromUrl(url);
    const outputDims = extractImageDimensions(url);

    // If we have both crop and output dimensions, convert to original image coordinates
    if (crop && outputDims && crop.width > 0 && crop.height > 0) {
      // Calculate scale factors
      const scaleX = crop.width / outputDims.width;
      const scaleY = crop.height / outputDims.height;

      // Scale marker position from output to crop space
      const markerInCropX = markerX * scaleX;
      const markerInCropY = markerY * scaleY;

      // Add crop offset to get original image coordinates
      return {
        x: Math.round(crop.x + markerInCropX),
        y: Math.round(crop.y + markerInCropY),
      };
    }

    // If no transformations found, return as-is
    // (assuming these are already in original coordinates)
    return {
      x: markerX,
      y: markerY,
    };
  } catch (error) {
    console.error('Error extracting marker from URL:', error);
    return null;
  }
}

/**
 * Removes all transformations from a Cloudinary URL to get the original image
 */
export function getOriginalCloudinaryUrl(url: string): string {
  if (!url || !url.includes('cloudinary.com')) {
    return url;
  }

  try {
    const parts = url.split('/upload/');
    if (parts.length !== 2) {
      return url;
    }

    const [baseUrl, afterUpload] = parts;
    const segments = afterUpload.split('/');

    // Find where the actual file path starts
    let filePathStartIndex = 0;
    for (let i = 0; i < segments.length; i++) {
      if (segments[i].includes(',') || segments[i].match(/^[a-z]_/)) {
        filePathStartIndex = i + 1;
      } else {
        break;
      }
    }

    const assetPath = segments.slice(filePathStartIndex).join('/');
    return `${baseUrl}/upload/${assetPath}`;
  } catch (error) {
    console.error('Error getting original Cloudinary URL:', error);
    return url;
  }
}
